import React, { FC } from 'react';
import { isFuture, isSameDay, parseISO } from 'date-fns';
import { BoxComponent, Button, Group, Heading, Pane, Popover, Table } from 'evergreen-ui';
import logger from '../logging';
import { CoverageTier, Member, MemberGroup } from '../models';
import { ActiveBadge } from './active-badge.component';
import { CoverageTierBadge } from './coverage-tier.component';

const log = logger(__filename);

export interface ClientStatisticsComponentProps {
  memberGroups: MemberGroup[];
  children?: never;
}

type Stats = StatsBy<'active' | 'terminated', StatsBy<CoverageTier, BaseStats>>;

type StatsBy<StatsGroup extends string, Value> = {
  [K in StatsGroup | 'all']: Value;
};

interface BaseStats {
  employeeCount: number;
  dependentCount: number;
  totalCount: number;
}

function createBaseStats(): BaseStats {
  const stats = {
    employeeCount: 0,
    dependentCount: 0,
    get totalCount() {
      return stats.employeeCount + stats.dependentCount;
    },
  };
  return stats;
}

function createStatsBy<K extends string, V>(keys: K[], createValue: () => V) {
  const stats: Record<string, V> = {
    all: createValue(),
  };

  for (const key of keys) {
    stats[key] = createValue();
  }

  return stats as StatsBy<K, V>;
}

function statusForMember(member: Member) {
  return (!member.benefitEndDate ||
    isFuture(parseISO(member.benefitEndDate)) ||
    isSameDay(parseISO(member.benefitEndDate), new Date())) &&
    !member.isCancelTerminated
    ? 'active'
    : 'terminated';
}

type Status = 'active' | 'terminated';
const statuses = ['active', 'terminated'] as Status[];
const coverageTiers = Object.values(CoverageTier);

function computeStats(memberGroups: MemberGroup[]): Stats {
  const stats = createStatsBy(statuses, () => createStatsBy(coverageTiers, createBaseStats));

  for (const group of memberGroups) {
    if (!group.coverageTier) {
      log.warn({ groupId: group.id }, 'Member group is missing coverage tier. Will be ignored in statistics');
      continue;
    }
    stats[statusForMember(group.primary)][group.coverageTier].employeeCount++;

    for (const member of group.dependents) {
      stats[statusForMember(member)][group.coverageTier].dependentCount++;
    }
  }

  for (const status of statuses) {
    for (const coverageTier of coverageTiers) {
      stats[status].all.employeeCount += stats[status][coverageTier].employeeCount;
      stats.all[coverageTier].employeeCount += stats[status][coverageTier].employeeCount;
      stats[status].all.dependentCount += stats[status][coverageTier].dependentCount;
      stats.all[coverageTier].dependentCount += stats[status][coverageTier].dependentCount;
    }

    stats.all.all.employeeCount += stats[status].all.employeeCount;
    stats.all.all.dependentCount += stats[status].all.dependentCount;
  }

  return stats;
}

const DetailedClientStatisticsComponent: FC<{ stats: Stats }> = ({ stats }) => {
  const [status, setStatus] = React.useState<keyof Stats>('active');
  return (
    <Pane padding={8} background="tint1">
      <Pane display="flex" flexDirection="row" gap={8} alignItems="baseline" justifyContent="center" marginBottom={8}>
        <Heading size={300}>Show</Heading>
        <Group>
          <Button appearance={status === 'active' ? 'selected' : 'default'} onClick={() => setStatus('active')}>
            <ActiveBadge active />
          </Button>
          <Button appearance={status === 'terminated' ? 'selected' : 'default'} onClick={() => setStatus('terminated')}>
            <ActiveBadge active={false} />
          </Button>
          <Button appearance={status === 'all' ? 'selected' : 'default'} onClick={() => setStatus('all')}>
            All
          </Button>
        </Group>
      </Pane>
      <Table width={500}>
        <Table.Head height={32}>
          <Table.TextHeaderCell flex="0 0 180px">Coverage Tier</Table.TextHeaderCell>
          <Table.TextHeaderCell textAlign="right">Employees</Table.TextHeaderCell>
          <Table.TextHeaderCell textAlign="right">Dependents</Table.TextHeaderCell>
          <Table.TextHeaderCell textAlign="right">Total</Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {(Object.values(CoverageTier) as CoverageTier[]).map(coverageTier => (
            <Table.Row height={32}>
              <Table.Cell flex="0 0 180px">
                <CoverageTierBadge coverageTier={coverageTier} />
              </Table.Cell>
              <Table.TextCell textAlign="right">
                {stats[status][coverageTier].employeeCount.toLocaleString()}
              </Table.TextCell>
              <Table.TextCell textAlign="right">
                {stats[status][coverageTier].dependentCount.toLocaleString()}
              </Table.TextCell>
              <Table.TextCell textAlign="right">
                {stats[status][coverageTier].totalCount.toLocaleString()}
              </Table.TextCell>
            </Table.Row>
          ))}
          <Table.Row height={32}>
            <Table.TextHeaderCell flex="0 0 180px">TOTAL</Table.TextHeaderCell>
            <Table.TextCell textAlign="right">{stats[status].all.employeeCount.toLocaleString()}</Table.TextCell>
            <Table.TextCell textAlign="right">{stats[status].all.dependentCount.toLocaleString()}</Table.TextCell>
            <Table.TextCell textAlign="right">{stats[status].all.totalCount.toLocaleString()}</Table.TextCell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Pane>
  );
};

export const ClientStatisticsComponent: BoxComponent<ClientStatisticsComponentProps, 'div'> = ({
  memberGroups,
  ...boxProps
}) => {
  const computeStatsMemo = React.useCallback(computeStats, []);
  const stats = computeStatsMemo(memberGroups);
  return (
    <Pane {...boxProps}>
      {stats.active.all.employeeCount.toLocaleString()} employees and {stats.active.all.dependentCount.toLocaleString()}{' '}
      dependents are <ActiveBadge active />
      <Popover content={<DetailedClientStatisticsComponent stats={stats} />}>
        <Button height={24} marginLeft={8}>
          More info
        </Button>
      </Popover>
    </Pane>
  );
};
